/**
 * The event-shell role read — the other half of the shell's authorization
 * state. Extracted for the same reason as the event read: the key AND the field
 * list were hand-copied into a second component, and a change to either would
 * silently split the cache in two.
 *
 * `useEventRoleIsStale` is the reason this file also exports a key helper:
 * surfaces that gate ACCESS (the onboarding setup journey) must know when the
 * capabilities they can see still belong to the PREVIOUS event — the shell
 * deliberately keeps those painted so its chrome doesn't flash VIEWER.
 */
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { getEventRoleByUserId } from "@/lib/api/handlers/EventRoleHandling";

export const EVENT_LAYOUT_ROLE_FIELDS =
  "role,templateId,capabilities,rowFilters,fieldAllowlist,legacyRoleFloor";

export function eventLayoutRoleKey(eventId: string | null | undefined) {
  return ["eventLayout", eventId, "role"];
}

export function useEventLayoutRoleQuery(
  eventId: string | null | undefined,
  options: { enabled?: boolean; keepPrevious?: boolean } = {},
) {
  const { enabled = true, keepPrevious = false } = options;
  return useQuery({
    queryKey: eventLayoutRoleKey(eventId),
    // Fetch the enriched EventRoleDTO in a SINGLE request so the
    // CapabilitiesProvider has capabilities/templateId/rowFilters/
    // fieldAllowlist/legacyRoleFloor for CUSTOM users without a second
    // roundtrip. For non-CUSTOM roles, the backend computes the implicit
    // capability set from Capability.IMPLICIT and returns it inline.
    queryFn: () => getEventRoleByUserId(eventId as string, EVENT_LAYOUT_ROLE_FIELDS),
    enabled: enabled && !!eventId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // Keep the previous role while the new event's role loads — avoids the
    // {role:""} fail-closed fallback flashing VIEWER chrome on every switch.
    ...(keepPrevious ? { placeholderData: keepPreviousData } : {}),
    // B2-0: the preview re-mint's cache bust leaves this read alone — it
    // resolves the MINTER's own row, not the previewed role.
    meta: { previewScopeIndependent: true },
  });
}

/**
 * Do we NOT yet know this event's role? True while the role for exactly this
 * eventId is still in flight — which is precisely when the shell is painting
 * the previous event's capabilities. Read from the cache rather than passed
 * down as a prop, so no wiring step can be forgotten.
 */
export function useEventRoleIsStale(eventId: string | null | undefined): boolean {
  const queryClient = useQueryClient();
  if (!eventId) return false;
  const state = queryClient.getQueryState(eventLayoutRoleKey(eventId));
  return !state || state.status === "pending";
}
